import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { addInstallTabs } from './install-tabs/InstallationDropdownData';
import EntityModal from '../../../components/common/tab-modal/TabModal';
import { useAppDispatch, useAppSelector } from '../../../hooks/storeHooks';
import Loader from '../../common/Loader/Loader';
import {
  InstallFailureData,
  InstallSystemDesign,
  InstallRunlife,
  InstallCompletions,
  InstallInstallation,
} from '../../../models/InstallModels';
import InstallFailureTab from './install-tabs/InstallFailureTab';
import {
  editInstallation,
  fetchInstallationHistoryData,
} from '../../../services/installationHistory/InstallationHistoryService';
import { ReactComponent as WarningIcon } from '../../../images/alert-triangle.svg';
import { useToastService } from '../../../components/toast/ToastService';
import FormField from '../../../components/common/FormField';
import '../Installation.scss';
import GenericFormTab from '../../../components/common/GenericFormTab';
import InstallInstallationTab from './install-tabs/InstallInstallationTab';
import {
  SystemDesignFieldConfigs,
  CompletionFieldConfigs,
  RunlifeFieldConfigs,
} from './install-tabs/InstallTabFieldConfig';
import InstallInstallationCostTab from './install-tabs/InstallInstallationCostTab';
import { parseApiDate } from '../../../utilities/completionsUtils';

interface CreateInstallModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  onSave?: () => void;
  isEditMode?: boolean;
}

const initialInstallationState: InstallInstallation = {
  WellId: 0,
  Well: '',
  WellIdentifier: '',
  LiftType: '',
  Manufacturer: '',
  ManufacturerController: '',
  InstallNumber: '',
  Status: 'Failed',
  InstallReportDocuments: [],
  PullReportDocuments: [],
  RcfaReportDocuments: [],
  IsPurchasedSystem: false,
  IsUsedSystem: false,
  IsExcluded: false,
};

const initialFailureState: InstallFailureData = {
  ReasonForPull: '',
  ReasonForPullDetail: '',
  Component: '',
  Subcomponent: '',
  IsInDirectFailure: false,
  FailureDescription: '',
  FailureCause: '',
  FailureCauseDetail: '',
  FailureCauseSpecific: '',
  FailureCorrectiveAction: '',
  FurtherInvestigation: '',
  OperatingComments: '',
  Observations: false,
  ObservationIds: [],
};

const initialSystemDesignState: InstallSystemDesign = {
  DesignDate: null,
  FluidAbovePump: '',
  TubingHeadPressure: '',
  FutureStaticBhp: '',
  SpecificGravityWater: '',
  ProductionGor: '',
  FutureWaterCutPercent: '',
  Co2Concentration: '',
  ReservoirTemperature: '',
  DesignDataSheetDocuments: [],
  VendorDesignDocuments: [],
  VendorQuoteDocuments: [],
};

const initialRunlifeState: InstallRunlife = {
  InstallDateString: null,
  StartDateString: null,
  FailDateString: null,
  PullDateString: null,
  TeardownDateString: null,
  RunlifeDays: 0,
};

const initialCompletionState: InstallCompletions = {
  PumpIntakeDepthMd: '',
  PumpIntakeDepthTvd: '',
  TopPerf: '',
  BottomPerf: '',
  TubingSize: '',
  CompletionDiagramDocuments: [],
};

const CreateInstallModal: React.FC<CreateInstallModalProps> = ({
  isOpen,
  onClose,
  title,
  isEditMode = false,
  onSave,
}) => {
  const { showSuccessToast } = useToastService();
  const dispatch = useAppDispatch();

  const addInstallData = useAppSelector((state: any) => state.createInstalls.addInstallationsData.data);
  const isEditModeData = useAppSelector((state: any) => state.createInstalls.editInstallationData.data);
  const addInstallLoading = useAppSelector((state: any) => state.createInstalls.addInstallationsData.loading);
  const editInstallationData = useAppSelector((state: any) => state.createInstalls.editInstallationData.loading);
  const saveLoading = useAppSelector((state: any) => state.createInstalls.editInstallations.loading);
  const wellData = useAppSelector((state: any) => state.createInstalls.wellData.data) || [];
  const wellId = useAppSelector((state: any) => state.WellTestIdSlice?.data?.WellId);

  const safeWellData = Array.isArray(wellData) ? wellData : [];

  const [activeTab, setActiveTab] = useState(addInstallTabs[0].id);
  const [installationData, setInstallationData] = useState<InstallInstallation>(initialInstallationState);
  const [runlifeData, setRunlifeData] = useState<InstallRunlife>(initialRunlifeState);
  const [completionData, setCompletionData] = useState<InstallCompletions>(initialCompletionState);
  const [failureData, setFailureData] = useState<InstallFailureData>(initialFailureState);
  const [systemDesignData, setSystemDesignData] = useState<InstallSystemDesign>(initialSystemDesignState);
  const [installationCostData, setInstallationCostData] = useState<any>({});
  const [blockUpdatesChecked, setBlockUpdatesChecked] = useState(false);
  const [completionWarnings, setCompletionWarnings] = useState<string[]>([]);

  const selectedTabs = useMemo(() => addInstallTabs, []);

  const tabErrors = () => {
    //TODO : Implement tab errors
  };

  const enhancedTabs = useMemo(
    () =>
      selectedTabs.map((tab) => ({
        ...tab,
      })),
    [selectedTabs, tabErrors],
  );

  const handleClose = useCallback(() => {
    setCompletionWarnings([]);
    onClose();
  }, [onClose]);

  function formatDate(date: any) {
    if (!date) return '';
    const d = new Date(date);
    if (isNaN(d.getTime())) return '';
    const day = d.getDate().toString().padStart(2, '0');
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = monthNames[d.getMonth()];
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  }

  const handleSave = async () => {
    setCompletionWarnings([]);
    const payload = {
      ManufacturerId: installationData.Manufacturer || '',
      ManufacturerControllerId: installationData.ManufacturerController || '',
      InstallDate: formatDate(runlifeData.InstallDateString),
      StartDate: formatDate(runlifeData.StartDateString),
      FailDate: formatDate(runlifeData.FailDateString),
      PullDate: formatDate(runlifeData.PullDateString),
      TeardownDate: formatDate(runlifeData.TeardownDateString),
      PumpIntakeDepthMd: completionData.PumpIntakeDepthMd || '',
      PumpIntakeDepthTvd: completionData.PumpIntakeDepthTvd || '',
      TopPerf: completionData.TopPerf || '',
      BottomPerf: completionData.BottomPerf || '',
      TubingSize: completionData.TubingSize || '',
      DesignDate: formatDate(systemDesignData.DesignDate),
      FluidAbovePump: systemDesignData.FluidAbovePump || '',
      TubingHeadPressure: systemDesignData.TubingHeadPressure || '',
      FutureStaticBhp: systemDesignData.FutureStaticBhp || '',
      SpecificGravityWater: systemDesignData.SpecificGravityWater || '',
      ProductionGor: systemDesignData.ProductionGor || '',
      FutureWaterCutPercent: systemDesignData.FutureWaterCutPercent || '',
      Co2Concentration: systemDesignData.Co2Concentration || '',
      ReservoirTemperature: systemDesignData.ReservoirTemperature || '',
      WorkoverCost: installationCostData.WorkoverCost || '0',
      PurchaseCost: installationCostData.PurchaseCost || '0',
      DailyCost: installationCostData.DailyCost || '0',
      TransferOwnershipDate: formatDate(installationCostData.TransferOwnershipDate),
      RepairCost: installationCostData.RepairCost || '0',
      AfeNumber: installationCostData.AfeNumber || '',
      ReasonForPull: failureData.ReasonForPull || '',
      ReasonForPullDetail: failureData.ReasonForPullDetail || '',
      Component: failureData.Component || '',
      FailureDescription: failureData.FailureDescription || '',
      FailureCause: failureData.FailureCause || '',
      FailureCauseDetail: failureData.FailureCauseDetail || '',
      FailureCauseSpecific: failureData.FailureCauseSpecific || '',
      FailureCorrectiveAction: failureData.FailureCorrectiveAction || '',
      FurtherInvestigation: failureData.FurtherInvestigation || '',
      OperatingComments: failureData.OperatingComments || '',
      LiftType: installationData.LiftType || '',
      Id: isEditModeData?.Id || '',
      WellId: installationData.WellId || '',
      WellIdentifier: installationData.WellIdentifier || '',
      Well: (installationData.Well || '').trim(),
      InstallNumber: isEditMode ? isEditModeData?.InstallNumber : 0,
      BlockUpdateOverride: blockUpdatesChecked ? 'True' : 'False',
      IsPurchasedSystem: installationData.IsPurchasedSystem ? 'true' : 'false',
      IsUsedSystem: installationData.IsUsedSystem ? 'true' : 'false',
      IsExcluded: installationData.IsExcluded ? 'true' : 'false',
      IsInDirectFailure: failureData.IsInDirectFailure ? 'true' : 'false',
    };

    // Helper function to convert document arrays to File arrays
    const convertToFileArray = (documents: any[]): File[] => {
      if (!Array.isArray(documents)) return [];
      return documents.filter((doc) => doc instanceof File);
    };

    const documents = {
      InstallReport: convertToFileArray(installationData.InstallReportDocuments),
      PullReport: convertToFileArray(installationData.PullReportDocuments),
      RcfaReport: convertToFileArray(installationData.RcfaReportDocuments),
      CompletionDiagram: convertToFileArray(completionData.CompletionDiagramDocuments),
      DesignDataSheet: convertToFileArray(systemDesignData.DesignDataSheetDocuments),
      VendorDesign: convertToFileArray(systemDesignData.VendorDesignDocuments),
      VendorQuote: convertToFileArray(systemDesignData.VendorQuoteDocuments),
    };

    try {
      const result = await dispatch(editInstallation({ payload, documents }));
      if (editInstallation.fulfilled.match(result)) {
        setCompletionWarnings([]);
        showSuccessToast('Installation saved successfully', 'Installation has been saved successfully.');
        if (wellId) {
          dispatch(fetchInstallationHistoryData(wellId));
        }
        if (onSave) onSave();
        handleClose();
      } else {
        let errorMessage = '';
        const errorPayload = result?.payload as { [key: string]: any };
        if (errorPayload && typeof errorPayload === 'object') {
          if (errorPayload.Validations) {
            errorMessage = errorPayload.Validations;
          } else {
            errorMessage = Object.values(errorPayload).flat().join('\n');
          }
        }
        if (!errorMessage) {
          errorMessage = 'Failed to save installation. Please try again.';
        }
        setCompletionWarnings([errorMessage]);
      }
    } catch (error) {
      setCompletionWarnings(['An unexpected error occurred while saving the installation.']);
    }
  };

  const handleTabChange = (nextTab: string) => {
    setCompletionWarnings([]);
    setActiveTab(nextTab);
  };

  const updateFormData = useCallback(() => {
    const sourceData = isEditMode ? isEditModeData : addInstallData;

    const wellName =
      sourceData?.WellId && safeWellData.length > 0
        ? safeWellData.find((w: any) => w.Key == sourceData.WellId)?.Value || ''
        : sourceData?.Well || '';

    setInstallationData({
      WellId: sourceData?.WellId || 0,
      Well: wellName,
      WellIdentifier: sourceData?.WellIdentifier || '',
      LiftType: sourceData?.LiftType || '',
      Manufacturer: sourceData?.ManufacturerId || '',
      ManufacturerController: sourceData?.ManufacturerControllerId || '',
      InstallNumber: isEditMode ? isEditModeData?.InstallNumber : 0,
      Status: sourceData?.Status || 'Failed',
      InstallReportDocuments: sourceData?.InstallReportDocuments || [],
      PullReportDocuments: sourceData?.PullReportDocuments || [],
      RcfaReportDocuments: sourceData?.RcfaReportDocuments || [],
      IsPurchasedSystem: sourceData?.IsPurchasedSystem || false,
      IsUsedSystem: sourceData?.IsUsedSystem || false,
      IsExcluded: sourceData?.IsExcluded || false,
    });
    setRunlifeData({
      ...initialRunlifeState,
      InstallDateString: parseApiDate(sourceData?.InstallDateString) || null,
      StartDateString: parseApiDate(sourceData?.StartDateString) || null,
      FailDateString: parseApiDate(sourceData?.FailDateString) || null,
      PullDateString: parseApiDate(sourceData?.PullDateString) || null,
      TeardownDateString: parseApiDate(sourceData?.TeardownDateString) || null,
      RunlifeDays: sourceData?.RunlifeDays || 0,
    });

    setCompletionData({
      ...initialCompletionState,
      PumpIntakeDepthMd: sourceData?.PumpIntakeDepthMd || '',
      PumpIntakeDepthTvd: sourceData?.PumpIntakeDepthTvd || '',
      TopPerf: sourceData?.TopPerf || '',
      BottomPerf: sourceData?.BottomPerf || '',
      TubingSize: sourceData?.TubingSize || '',
      CompletionDiagramDocuments: sourceData?.CompletionDiagramDocuments || [],
    });
    setSystemDesignData({
      ...initialSystemDesignState,
      DesignDate: parseApiDate(sourceData?.DesignDate) || null,
      FluidAbovePump: sourceData?.FluidAbovePump || '',
      TubingHeadPressure: sourceData?.TubingHeadPressure || '',
      FutureStaticBhp: sourceData?.FutureStaticBhp || '',
      SpecificGravityWater: sourceData?.SpecificGravityWater || '',
      ProductionGor: sourceData?.ProductionGor || '',
      FutureWaterCutPercent: sourceData?.FutureWaterCutPercent || '',
      Co2Concentration: sourceData?.Co2Concentration || '',
      ReservoirTemperature: sourceData?.ReservoirTemperature || '',
      DesignDataSheetDocuments: sourceData?.DesignDataSheetDocuments || [],
      VendorDesignDocuments: sourceData?.VendorDesignDocuments || [],
      VendorQuoteDocuments: sourceData?.VendorQuoteDocuments || [],
    });
    setFailureData({
      ...initialFailureState,
      ReasonForPull: sourceData?.ReasonForPull || '',
      ReasonForPullDetail: sourceData?.ReasonForPullDetail || '',
      Component: sourceData?.Component || '',
      Subcomponent: sourceData?.Subcomponent || '',
      IsInDirectFailure: sourceData?.IsInDirectFailure || false,
      FailureDescription: sourceData?.FailureDescription || '',
      FailureCause: sourceData?.FailureCause || '',
      FailureCauseDetail: sourceData?.FailureCauseDetail || '',
      FailureCauseSpecific: sourceData?.FailureCauseSpecific || '',
      FailureCorrectiveAction: sourceData?.FailureCorrectiveAction || '',
      FurtherInvestigation: sourceData?.FurtherInvestigation || '',
      OperatingComments: sourceData?.OperatingComments || '',
      Observations: sourceData?.Observations || false,
      ObservationIds: sourceData?.ObservationIds || [],
    });
    setInstallationCostData({
      WorkoverCost: sourceData?.WorkoverCost || 0,
      RunlifeDays: sourceData?.RunlifeDays || 0,
      DailyCost: sourceData?.DailyCost || 0,
      MonthlyCost: sourceData?.MonthlyCost || 0,
      TransferOwnershipDate: parseApiDate(sourceData?.TransferOwnershipDate) || null,
      RentalCost: sourceData?.RentalCost || 0,
      RepairCost: sourceData?.RepairCost || 0,
      TotalCost: sourceData?.TotalCost || 0,
      AfeNumber: sourceData?.AfeNumber || 0,
    });
  }, [isEditMode, isEditModeData, addInstallData, wellData]);

  useEffect(() => {
    if (isOpen) {
      setActiveTab('installation');
      updateFormData();
      if (isEditMode && isEditModeData && typeof isEditModeData.BlockUpdateOverride !== 'undefined') {
        setBlockUpdatesChecked(
          isEditModeData.BlockUpdateOverride === true || isEditModeData.BlockUpdateOverride === 'True',
        );
      } else {
        setBlockUpdatesChecked(false);
      }
    }
  }, [isOpen, updateFormData, isEditMode, isEditModeData]);

  const renderTabContent = useCallback(() => {
    switch (activeTab) {
      case 'installation':
        return <InstallInstallationTab value={installationData} onChange={setInstallationData} />;
      case 'runlife':
        return (
          <GenericFormTab fieldConfigs={RunlifeFieldConfigs} formData={runlifeData} setFormData={setRunlifeData} />
        );
      case 'completion':
        return (
          <GenericFormTab
            fieldConfigs={CompletionFieldConfigs}
            formData={completionData}
            setFormData={setCompletionData}
          />
        );
      case 'systemDesign':
        return (
          <GenericFormTab
            fieldConfigs={SystemDesignFieldConfigs}
            formData={systemDesignData}
            setFormData={setSystemDesignData}
          />
        );
      case 'failureData':
        return (
          <InstallFailureTab
            failureData={failureData}
            setFailureData={setFailureData}
            installationId={isEditModeData?.Id}
            isEditMode={isEditMode}
          />
        );
      case 'installationCost':
        return (
          <InstallInstallationCostTab
            value={installationCostData}
            onChange={setInstallationCostData}
            IsPurchasedSystem={!!installationData?.IsPurchasedSystem}
            startDate={runlifeData?.StartDateString || null}
          />
        );
      default:
        return null;
    }
  }, [activeTab, installationData, failureData, systemDesignData, runlifeData, completionData, installationCostData]);

  useEffect(() => {
    if (!isOpen) {
      setCompletionWarnings([]);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const isLoading = addInstallLoading || editInstallationData || saveLoading;

  return (
    <EntityModal
      isOpen={isOpen}
      onClose={handleClose}
      title={title}
      onSave={handleSave}
      enhancedTabs={enhancedTabs}
      activeTab={activeTab}
      onTabChange={handleTabChange}
      isSubmitting={isLoading}
      customHeaderContent={
        <div className='install-modal-header-actions'>
          <FormField
            label='Block updates'
            name='BlockUpdateOverride'
            type='checkbox'
            value={blockUpdatesChecked}
            onChange={() => setBlockUpdatesChecked((prev) => !prev)}
            isCheckboxRight
          />
        </div>
      }
    >
      {completionWarnings.length > 0 && (
        <div className='installation-warnings' style={{ margin: '0px  0px 16px 0px' }}>
          <div className='warnings-header'>
            <WarningIcon className='warning-icon' />
            <div className='warnings-header-text-and-list'>
              <div className='warnings-content'>
                {completionWarnings.map((warning: string, index: number) => (
                  <div key={index} className='warning-item'>
                    {warning}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
      {isLoading ? (
        <div className='completion-modal-loading' style={{ padding: '40px', textAlign: 'center' }}>
          <Loader />
        </div>
      ) : (
        renderTabContent()
      )}
    </EntityModal>
  );
};

export default CreateInstallModal;
